import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAiAssistantStore } from '@/store/ai-assistant.store';
import { useMessagingStore } from '@/store/messaging.store';
import { useKeyboardShortcutsModal } from './useKeyboardShortcutsModal';
import { toast } from '@/utils/toast';

const SHORTCUTS_HINT_KEY = 'cmdbridge-shortcuts-hint-shown';

interface GlobalShortcutsOptions {
  /** Callback to close the mobile sidebar */
  onEscape?: () => void;
}

const CHORD_TIMEOUT_MS = 600;

/** Navigation targets for G-chord sequences */
const CHORD_NAV: Record<string, { path: string; description: string }> = {
  d: { path: '/', description: 'Go to Dashboard' },
  m: { path: '/map', description: 'Go to Map' },
  i: { path: '/incidents', description: 'Go to Incidents' },
  t: { path: '/tasks', description: 'Go to Tasks' },
  f: { path: '/forms', description: 'Go to Forms' },
  s: { path: '/shelters', description: 'Go to Shelters' },
  u: { path: '/team', description: 'Go to Team' },
  a: { path: '/audit-logs', description: 'Go to Audit Logs' },
  c: { path: '/settings', description: 'Go to Settings' },
};

function isInputFocused(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  // SELECT belongs here with INPUT and TEXTAREA. A native select does type-ahead:
  // typing "r" in a status dropdown is how you jump to "Responding". Without
  // this the letter fell through to the single-key shortcuts and navigated the
  // operator off the page instead. useTaskKeyboardShortcuts already guarded it.
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
  );
}

/**
 * Global keyboard shortcuts hook.
 *
 * Handles chord sequences (G then D/M/I/T/F/S/U/A/C) for navigation,
 * single-key shortcuts (/, R, Escape), and modifier shortcuts (Ctrl+/).
 *
 * Mount once in MainLayout.
 */
export function useGlobalKeyboardShortcuts({ onEscape }: GlobalShortcutsOptions = {}) {
  const navigate = useNavigate();
  const toggleAiPanel = useAiAssistantStore((s) => s.togglePanel);
  const toggleMessaging = useMessagingStore((s) => s.togglePanel);
  const trackShortcutUsage = useKeyboardShortcutsModal((s) => s.trackShortcutUsage);

  // Chord state refs (avoid re-renders)
  const chordPending = useRef(false);
  const chordTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep all callbacks fresh via refs to avoid useEffect re-registration
  const onEscapeRef = useRef(onEscape);
  const navigateRef = useRef(navigate);
  const toggleAiPanelRef = useRef(toggleAiPanel);
  const toggleMessagingRef = useRef(toggleMessaging);
  const trackShortcutUsageRef = useRef(trackShortcutUsage);
  useEffect(() => {
    onEscapeRef.current = onEscape;
    navigateRef.current = navigate;
    toggleAiPanelRef.current = toggleAiPanel;
    toggleMessagingRef.current = toggleMessaging;
    trackShortcutUsageRef.current = trackShortcutUsage;
  }, [onEscape, navigate, toggleAiPanel, toggleMessaging, trackShortcutUsage]);

  // First-visit shortcut hint
  useEffect(() => {
    if (localStorage.getItem(SHORTCUTS_HINT_KEY)) return;
    const timer = setTimeout(() => {
      toast.info('Tip: Press ? to view keyboard shortcuts');
      localStorage.setItem(SHORTCUTS_HINT_KEY, 'true');
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const resetChord = useCallback(() => {
    chordPending.current = false;
    if (chordTimer.current) {
      clearTimeout(chordTimer.current);
      chordTimer.current = null;
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      const hasModifier = e.ctrlKey || e.metaKey || e.altKey;
      const inInput = isInputFocused(e.target);

      // --- Modifier shortcuts (work even in inputs) ---

      // Ctrl+/ or Cmd+/ → Toggle AI Assistant
      if (key === '/' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        toggleAiPanelRef.current();
        trackShortcutUsageRef.current(['Ctrl', '/'], 'Toggle AI Assistant');
        resetChord();
        return;
      }

      // Ctrl+M or Cmd+M → Toggle Messaging panel
      if (key === 'm' && (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        toggleMessagingRef.current();
        trackShortcutUsageRef.current(['Ctrl', 'M'], 'Toggle messaging panel');
        resetChord();
        return;
      }

      // --- Non-input shortcuts (skip when typing) ---
      if (inInput) return;

      // Escape → close sidebar
      if (key === 'escape') {
        onEscapeRef.current?.();
        resetChord();
        return;
      }

      // Skip if other modifiers are held (let browser/other handlers deal with them)
      if (hasModifier) {
        resetChord();
        return;
      }

      // --- Chord handling: G then <key> ---
      if (chordPending.current) {
        const nav = CHORD_NAV[key];
        if (nav) {
          e.preventDefault();
          navigateRef.current(nav.path);
          trackShortcutUsageRef.current(['G', key.toUpperCase()], nav.description);
        }
        resetChord();
        return;
      }

      if (key === 'g') {
        chordPending.current = true;
        chordTimer.current = setTimeout(resetChord, CHORD_TIMEOUT_MS);
        return;
      }

      // --- Single-key shortcuts ---

      // / → Focus first visible search input
      if (key === '/') {
        const searchInput = document.querySelector<HTMLInputElement>(
          'input[type="search"], input[type="text"][placeholder*="earch"], input[placeholder*="earch"]'
        );
        if (searchInput) {
          e.preventDefault();
          searchInput.focus();
          trackShortcutUsageRef.current(['/'], 'Focus search field');
        }
        return;
      }

      // R → Refresh current view
      if (key === 'r') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('keyboard-refresh'));
        trackShortcutUsageRef.current(['R'], 'Refresh current view');
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      resetChord();
    };
  }, [resetChord]);
}
